import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Button } from 'reactstrap';
import { connect } from 'react-redux';

import Modal from '../../../common/modal/Modal';
import MaintenanceChecklistAdditionalExecutionForm from './MaintenanceChecklistAdditionalExecutionForm';

import {
  getMaintenanceChecklist,
  putMaintenanceChecklistAdditionalExecution,
} from '../../../../actions';

import { tzNormalizeDate } from '../../../../utils/date';

const toArray = (value) => {
  if (!value) return [];

  return Array.isArray(value) ? value : [value];
};

const normalizeDate = (value) =>
  value
    ? tzNormalizeDate({
        date: value,
      })
    : null;

const getId = (value) => {
  if (!value) return null;

  if (typeof value === 'number' || typeof value === 'string') {
    return value;
  }

  return (
    value.value ||
    value.key ||
    value.code ||
    value.name ||
    value.employeeId ||
    value.mechanicId ||
    value.id ||
    null
  );
};

const getEmployeeId = (employee) => {
  if (!employee) return null;

  if (typeof employee === 'number' || typeof employee === 'string') {
    return employee;
  }

  if (employee.employeeId) {
    return getId(employee.employeeId);
  }

  if (employee.employee) {
    return getId(employee.employee);
  }

  if (employee.mechanic) {
    return getId(employee.mechanic);
  }

  return getId(employee);
};

const buildMechanicList = (item) => {
  const mechanics = toArray(
    item.mechanics ||
      item.employeeList ||
      item.employee ||
      item.employeeId ||
      item.maintenancePlanningActivityExecutionEmployeeList,
  );

  return mechanics
    .map((employee) => ({
      employeeId: getEmployeeId(employee),
    }))
    .filter((employee) => employee.employeeId);
};

const normalizeNestedActivityExecutionList = (list) =>
  toArray(list).map((item) => ({
    ...item,

    maintenanceFailModeId: getId(item.maintenanceFailModeId),
    maintenanceFailModeCategoryId: getId(item.maintenanceFailModeCategoryId),
    activityId: getId(item.activityId),

    maintenanceWarningType: getId(item.maintenanceWarningType),
    recurrence: getId(item.recurrence),

    status: getId(item.status),

    startDate: normalizeDate(item.startDate),
    endDate: normalizeDate(item.endDate),
    workingHours: item.workingHours || null,

    advancePercentage: item.advancePercentage || null,

    comment:
      item.comment ||
      item.comments ||
      item.executionComment ||
      item.observations ||
      '',

    maintenancePlanningActivityExecutionEmployeeList: buildMechanicList(item),

    nestedActivityExecutionList: normalizeNestedActivityExecutionList(
      item.nestedActivityExecutionList,
    ),
  }));

const normalizeAdditionalActivityExecutionList = (list) =>
  toArray(list).map((item) => ({
    ...item,

    maintenanceChecklistActivityId: getId(item.maintenanceChecklistActivityId),

    maintenancePlanningActivityExecutionId: getId(
      item.maintenancePlanningActivityExecutionId,
    ),

    maintenanceFailModeId: getId(item.maintenanceFailModeId),

    maintenanceFailModeCategoryId: getId(item.maintenanceFailModeCategoryId),

    activityId: getId(item.activityId),

    status: getId(item.status),

    startDate: normalizeDate(item.startDate),
    endDate: normalizeDate(item.endDate),
    workingHours: item.workingHours || null,

    advancePercentage: item.advancePercentage || null,

    comment:
      item.comment ||
      item.comments ||
      item.executionComment ||
      item.observations ||
      '',

    maintenancePlanningActivityExecutionEmployeeList: buildMechanicList(item),

    nestedActivityExecutionList: normalizeNestedActivityExecutionList(
      item.nestedActivityExecutionList,
    ),
  }));

const normalizeAdditionalExecutionRequest = (values) => ({
  maintenanceChecklistId: values.maintenanceChecklistId,
  serviceOrderId: values.serviceOrderId,
  registeredBusId: values.registeredBusId,

  maintenanceChecklistAdditionalActivityExecutionList:
    normalizeAdditionalActivityExecutionList(
      values.maintenanceChecklistAdditionalActivityExecutionList,
    ),
});

const hasAdditionalActivities = (maintenanceChecklist) =>
  toArray(
    maintenanceChecklist &&
      maintenanceChecklist.maintenanceChecklistActivityList,
  ).some((activity) => activity.basic === false || activity.basic === null);

const MaintenanceChecklistAdditionalExecutionButton = ({
  maintenanceChecklist,
  dispatchPutMaintenanceChecklistAdditionalExecution,
  dispatchGetMaintenanceChecklist,
  disabled,
}) => {
  const [showModal, setShowModal] = useState(false);

  const canOpen = useMemo(
    () =>
      maintenanceChecklist &&
      maintenanceChecklist.id &&
      hasAdditionalActivities(maintenanceChecklist),
    [maintenanceChecklist],
  );

  const closeModal = () => {
    setShowModal(false);
  };

  const onModalSubmit = async (values) => {
    const request = normalizeAdditionalExecutionRequest(values);

    const response = await dispatchPutMaintenanceChecklistAdditionalExecution({
      maintenanceChecklistId: maintenanceChecklist.id,

      maintenanceChecklistAdditionalActivityExecutionUpdateRequest: request,
    });

    if (response) {
      await dispatchGetMaintenanceChecklist({
        maintenanceChecklistId: maintenanceChecklist.id,
      });

      closeModal();
    }
  };

  return (
    <>
      <Modal
        show={showModal}
        title="Ejecutar Adicionales"
        body={
          showModal ? (
            <MaintenanceChecklistAdditionalExecutionForm
              maintenanceChecklist={maintenanceChecklist}
              onSubmit={onModalSubmit}
            />
          ) : null
        }
        size="xl"
        onClickCancel={closeModal}
        onClickClose={closeModal}
      />

      <Button
        color="warning"
        onClick={() => setShowModal(true)}
        disabled={disabled || !canOpen}
      >
        <i className="fa fa-wrench" /> Ejecutar Adicionales
      </Button>
    </>
  );
};

MaintenanceChecklistAdditionalExecutionButton.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  maintenanceChecklist: PropTypes.object.isRequired,

  dispatchPutMaintenanceChecklistAdditionalExecution: PropTypes.func.isRequired,

  dispatchGetMaintenanceChecklist: PropTypes.func.isRequired,

  disabled: PropTypes.bool,
};

MaintenanceChecklistAdditionalExecutionButton.defaultProps = {
  disabled: false,
};

const mapDispatchToProps = {
  dispatchPutMaintenanceChecklistAdditionalExecution:
    putMaintenanceChecklistAdditionalExecution,

  dispatchGetMaintenanceChecklist: getMaintenanceChecklist,
};

export default connect(
  null,
  mapDispatchToProps,
)(MaintenanceChecklistAdditionalExecutionButton);
